/**
 * DTI v3 Public API Type Definitions
 *
 * This file defines the public TypeScript types (including branded strings).
 * The Zod schemas in `schemas.ts` are the runtime parsers that validate and
 * transform raw GraphQL payloads into these types.
 *
 * Important:
 * - `schemas.ts` is the parser source of truth (decode/validation happens there)
 * - Construct branded values via schemas + `parseBranded` / `unsafeParse`
 *
 * @see src/core/dtiPublicApi/schemas.ts - Zod schemas and type derivation
 */

import type {
  AgeGroupCode,
  AgeGroupLabel,
  AppointmentId,
  AvailabilityFetchStatus as AvailabilityFetchStatusFromSchema,
  BookingErrorCode as BookingErrorCodeFromSchema,
  BookingSource as BookingSourceFromSchema,
  BrowserTimezone as BrowserTimezoneFromSchema,
  CounselingStyleCode,
  DtiConfigActionName,
  DtiConfigConditionField,
  DtiConfigConditionValue,
  DtiConfigFieldName,
  DtiConfigQueryName,
  DtiConfigStepName,
  EligibilityStatus as EligibilityStatusFromSchema,
  FileAttachmentId,
  InsuranceCompanyName,
  InsuranceStatus as InsuranceStatusFromSchema,
  LeadInviteToken,
  MagicLinkReason,
  MagicLinkToken,
  MembershipInterval,
  MembershipPackageDescription,
  MembershipPackageId,
  MembershipPackageName,
  OtherCompanyName,
  PartnerAgeGroup,
  PartnerName,
  PromoCode,
  ProviderAvailabilityErrorCode as ProviderAvailabilityErrorCodeFromSchema,
  ProviderBio,
  ProviderDisplayName,
  PublicLeadBookingStatus,
  ReasonForVisitCode,
  SpecialtyCode,
  SponsorCode,
  StripeToken,
  Url,
} from '@/core/dtiPublicApi/schemas';

export type {
  LeadInviteToken,
  MagicLinkToken,
  ProviderBio,
} from '@/core/dtiPublicApi/schemas';

declare const brand: unique symbol;
declare const emailBrand: unique symbol;
declare const leadIdBrand: unique symbol;
declare const providerIdBrand: unique symbol;
declare const insuranceCompanyIdBrand: unique symbol;
declare const selfPayInsuranceSelectionValueBrand: unique symbol;
declare const usPhoneBrand: unique symbol;
declare const normalizedStateCodeBrand: unique symbol;
declare const usStateCodeBrand: unique symbol;
declare const firstNameBrand: unique symbol;
declare const lastNameBrand: unique symbol;
declare const maskedDateBrand: unique symbol;
declare const maskedEmailBrand: unique symbol;
declare const maskedPhoneBrand: unique symbol;
declare const instantBrand: unique symbol;

export type Brand<TValue, TName extends string> = TValue & {
  readonly [brand]: TName;
};

export type NonEmptyArray<T> = readonly [T, ...T[]];

export type BrowserTimezone = BrowserTimezoneFromSchema;

export type Email = string & {
  readonly [emailBrand]: 'Email';
};
export type FirstName = string & {
  readonly [firstNameBrand]: 'FirstName';
};
export type IsoDate = Brand<string, 'IsoDate'>;
export type LastName = string & {
  readonly [lastNameBrand]: 'LastName';
};
export type LeadId = string & {
  readonly [leadIdBrand]: 'LeadId';
};
export type ProviderId = string & {
  readonly [providerIdBrand]: 'ProviderId';
};
export type InsuranceCompanyId = string & {
  readonly [insuranceCompanyIdBrand]: 'InsuranceCompanyId';
};
declare const insuranceSearchCodeBrand: unique symbol;
export type InsuranceSearchCode = string & {
  readonly [insuranceSearchCodeBrand]: 'InsuranceSearchCode';
};
export type SelfPayInsuranceSelectionValue = 'none' & {
  readonly [selfPayInsuranceSelectionValueBrand]: 'SelfPayInsuranceSelectionValue';
};
export type UsPhone = string & {
  readonly [usPhoneBrand]: 'UsPhone';
};
export type ZipCode = Brand<string, 'ZipCode'>;
export type IsoTimestamp = Brand<string, 'IsoTimestamp'>;
/** @deprecated Use IsoTimestamp. */
export type IsoUtcTimestamp = IsoTimestamp;
export type Instant = Date & {
  readonly [instantBrand]: 'Instant';
};
export type MaskedDate = string & {
  readonly [maskedDateBrand]: 'MaskedDate';
};
export type MaskedEmail = string & {
  readonly [maskedEmailBrand]: 'MaskedEmail';
};
export type MaskedPhone = string & {
  readonly [maskedPhoneBrand]: 'MaskedPhone';
};

// ============================================================================
// CODE VALUE ARRAYS (source of truth for closed vocabularies)
// ============================================================================

export const SPECIALTY_CODE_VALUES = [
  'weight_management',
  'diabetes',
  'pcos',
  'heart_health',
  'gi_health',
  'sports_nutrition',
  'eating_disorders',
  'pregnancy_postpartum',
] as const;

export type SpecialtyCodeValue = (typeof SPECIALTY_CODE_VALUES)[number];

export const REFERRAL_SOURCE_VALUE_NAMES = [
  'GOOGLE_AD',
  'FACEBOOK_AD',
  'INSTAGRAM_AD',
  'YOUTUBE',
  'REDDIT',
  'GOOGLE_SEARCH',
  'SOCIAL_MEDIA',
  'TV_APPEARANCE',
  'PODCAST',
  'LINK_ON_OTHER_WEBSITE',
  'EMAIL',
  'PHYSICIAN',
  'FRIEND',
  'EVENT',
  'ZOCDOC',
  'INSURANCE_COMPANY',
  'SNAQ',
  'CLOSEKNIT',
  'OTHER',
] as const;

export type ReferralSourceValueName =
  (typeof REFERRAL_SOURCE_VALUE_NAMES)[number];

export type ReferralSourceInput = {
  value: ReferralSourceValueName;
  other?: string;
  details?: string;
  physicianPracticeName?: string;
  physicianName?: string;
};

export const US_STATE_CODE_VALUES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;

export type UsStateCodeValue = (typeof US_STATE_CODE_VALUES)[number];
export const NORMALIZED_STATE_CODE_VALUES = [
  ...US_STATE_CODE_VALUES,
  'AS',
  'GU',
  'MP',
  'PR',
  'VI',
] as const;

export type NormalizedStateCodeValue =
  (typeof NORMALIZED_STATE_CODE_VALUES)[number];
export type NormalizedStateCode = NormalizedStateCodeValue & {
  readonly [normalizedStateCodeBrand]: 'NormalizedStateCode';
};
// UsStateCode is a nominal subtype of NormalizedStateCode: every US state code
// is also a valid normalized state code, so widening is a no-op coercion.
export type UsStateCode = UsStateCodeValue &
  NormalizedStateCode & {
    readonly [usStateCodeBrand]: 'UsStateCode';
  };

export function brandUsStateCode(value: UsStateCodeValue): UsStateCode {
  return value as UsStateCode;
}

export function brandNormalizedStateCode(
  value: NormalizedStateCodeValue,
): NormalizedStateCode {
  return value as NormalizedStateCode;
}

export function widenUsStateCode(value: UsStateCode): NormalizedStateCode {
  return value;
}

export type InsuranceCompany = {
  id: InsuranceCompanyId;
  name: string;
  insuranceSearchCode: InsuranceSearchCode;
};

export type PublicSpecialty = {
  code: SpecialtyCode;
  name: string | null;
};

export type PublicCounselingStyle = {
  code: CounselingStyleCode;
  name: string;
};

export type PublicAgeGroup = {
  code: AgeGroupCode;
  name: AgeGroupLabel;
  label: AgeGroupLabel;
};

export type ProviderAvailabilityPreview = {
  slots: Availability[];
};

export type ProviderSummary = {
  id: ProviderId;
  firstName: ProviderDisplayName | null;
  lastName: ProviderDisplayName | null;
  bio: ProviderBio | null;
  photoUrl: Url | null;
  states: NormalizedStateCode[];
  reasonsForVisit: ReasonForVisitCode[];
  nextAvailableSlot: IsoTimestamp | null;
  nextAvailableDate: IsoDate | null;
  availabilityPreview: ProviderAvailabilityPreview | null;
  specialties: PublicSpecialty[];
  counselingStyles: PublicCounselingStyle[];
  ageGroups: PublicAgeGroup[];
  /**
   * `null` is a client-side sentinel only (`createUnknownProviderSummary`); the
   * wire schema is `z.boolean()`, so parsed search data is always `true`/`false`.
   * If "unknown" ever becomes a real backend state, `providerSummarySchema`
   * must gain `.nullable()` too.
   */
  acceptingNewPatients: boolean | null;
};

export type SearchProvidersReasonForVisitCriterion =
  | Readonly<{ tag: 'single'; code: ReasonForVisitCode }>
  | Readonly<{
      tag: 'multiple';
      codes: readonly [ReasonForVisitCode, ...ReasonForVisitCode[]];
    }>;

export type SearchProvidersCounselingStyleCriterion =
  | Readonly<{ tag: 'single'; code: CounselingStyleCode }>
  | Readonly<{
      tag: 'multiple';
      codes: readonly [CounselingStyleCode, ...CounselingStyleCode[]];
    }>;

export type SearchProvidersQuery = {
  state: UsStateCode;
  insuranceSearchCode?: InsuranceSearchCode;
  insurance?: never;
  reasonForVisit?: SearchProvidersReasonForVisitCriterion;
  timeOfDay?: TimeOfDay;
  dayOfWeek?: DayOfWeek;
  ageGroup?: AgeGroupCode;
  availabilityDate?: IsoTimestamp;
  counselingStyle?: SearchProvidersCounselingStyleCriterion;
  timezone?: BrowserTimezone;
  sponsorCode?: string;
  search?: string;
  page?: number;
  perPage?: number;
};

export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export type TimeOfDay = 'MORNING' | 'AFTERNOON' | 'EVENING';

export type AvailabilityFetchStatus = AvailabilityFetchStatusFromSchema;

export type SearchProvidersResult = {
  providers: ProviderSummary[];
  total: number;
  page: number;
  perPage: number;
  broadened: boolean;
  availabilityFetchStatus: AvailabilityFetchStatus;
  earliestAvailableSlot?: IsoTimestamp | null;
  availabilityWindowStart?: IsoTimestamp | null;
  availabilityWindowEnd?: IsoTimestamp | null;
  nextAvailabilityWindowStart?: IsoTimestamp | null;
  hasNextAvailabilityWindow?: boolean | null;
};

export type Availability = {
  startTime: IsoTimestamp;
};

export type ProviderAvailabilityErrorCode =
  ProviderAvailabilityErrorCodeFromSchema;

type ProviderAvailabilityBase = {
  providerId: ProviderId;
  availabilities: Availability[];
  intervalStart: IsoTimestamp | null;
  intervalEnd: IsoTimestamp | null;
  nextIntervalStart: IsoTimestamp | null;
  hasNextInterval: boolean;
};

export type ProviderAvailabilitySuccess = ProviderAvailabilityBase & {
  tag: 'available';
  errorCode: null;
};

export type ProviderAvailabilityFailure = ProviderAvailabilityBase & {
  tag: 'error';
  errorCode: ProviderAvailabilityErrorCode;
};

export type ProviderAvailabilityResult =
  ProviderAvailabilitySuccess | ProviderAvailabilityFailure;

export type FieldErrorCode = 'AGE_INELIGIBLE';

export type FieldError<TFieldName extends string = string> = {
  field: readonly TFieldName[];
  messages: string[];
  code: FieldErrorCode | null;
};

export function isAgeIneligibleError(error: FieldError): boolean {
  return error.code === 'AGE_INELIGIBLE';
}

export type ValidationErrorResult = {
  tag: 'validation_error';
  errors: FieldError[];
};

export type DateOfBirthInput = {
  year: number;
  month: number;
  day: number;
};

export type AppointmentSelectionInput = {
  providerId: ProviderId;
  startTime: IsoTimestamp;
  browserTimezone?: string | null;
  ageGroup?: AgeGroupCode;
  source?: BookingSourceFromSchema;
};

export type HeightWeightInput = {
  heightFeet?: number;
  heightInches?: number;
  weightLbs?: number;
};

export type ReferralParametersInput = {
  referringSource?: string;
  referringPhysician?: string;
  referringPractice?: string;
  referrerName?: string;
  referrerId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

export const INSURANCE_SELECTION_LITERALS = ['other', 'none'] as const;
export const RETIRED_INSURANCE_SELECTION_LITERALS = ['out_of_network'] as const;

export type InsuranceSelectionLiteral =
  (typeof INSURANCE_SELECTION_LITERALS)[number];
export const SELF_PAY_INSURANCE_SELECTION_VALUE =
  'none' as SelfPayInsuranceSelectionValue;

export type InsuranceSelectionValue =
  InsuranceCompanyId | InsuranceSelectionLiteral;

export function isInsuranceSelectionLiteral(
  value: string,
): value is InsuranceSelectionLiteral {
  return (INSURANCE_SELECTION_LITERALS as readonly string[]).includes(value);
}

export function isReservedInsuranceSelectionValue(value: string): boolean {
  return (
    isInsuranceSelectionLiteral(value) ||
    (RETIRED_INSURANCE_SELECTION_LITERALS as readonly string[]).includes(value)
  );
}

export function isSelfPayInsuranceSelectionValue(
  value: string,
): value is SelfPayInsuranceSelectionValue {
  return value === SELF_PAY_INSURANCE_SELECTION_VALUE;
}

export function isSponsorInsuranceSelectionValue(
  value: InsuranceSelectionValue | null | undefined,
): value is InsuranceCompanyId {
  return (
    value !== null && value !== undefined && !isInsuranceSelectionLiteral(value)
  );
}

export const SUBSCRIBER_RELATIONSHIPS = [
  'SELF',
  'SPOUSE',
  'CHILD',
  'OTHER',
] as const;

export type SubscriberRelationship = (typeof SUBSCRIBER_RELATIONSHIPS)[number];

export function isSubscriberRelationship(
  value: unknown,
): value is SubscriberRelationship {
  return (SUBSCRIBER_RELATIONSHIPS as readonly unknown[]).includes(value);
}

export type PolicyholderInput = {
  firstName: string;
  lastName: string;
  memberId?: string;
  dateOfBirth?: IsoDate;
};

export type Subscriber =
  | Readonly<{ relationship: 'SELF' }>
  | Readonly<{
      relationship: Exclude<SubscriberRelationship, 'SELF'>;
      policyholder: PolicyholderInput;
    }>;

export type InsuranceSelectionInput = {
  selectionValue?: InsuranceSelectionValue;
  otherCompanyName?: string;
  memberId?: string;
  groupId?: string;
  streetAddress?: string;
  city?: string;
  zip?: string;
  subscriber?: Subscriber;
};

export type LeadFieldsInput = {
  firstName?: string;
  lastName?: string;
  phone?: UsPhone;
  smsConsent?: boolean;
  appointmentConsent?: boolean;
  stateCd?: UsStateCode;
  reasonForVisitCode?: string;
  dateOfBirth?: DateOfBirthInput;
  insurance?: InsuranceSelectionInput;
  referralSource?: ReferralSourceInput;
  appointment?: AppointmentSelectionInput;
  sponsorCode?: string;
  accessCode?: string;
  heightWeight?: HeightWeightInput;
  referralParameters?: ReferralParametersInput;
};

export type PublicLeadHeightWeight = {
  heightFeet: number | null;
  heightInches: number | null;
  weightLbs: number | null;
};

export type PublicLeadInsuranceSelection = {
  type: 'SPONSOR' | 'SELF_PAY' | 'OTHER';
  networkStatus: InsuranceStatus;
  sponsorId: InsuranceCompanyId | null;
  otherCompanyName: OtherCompanyName | null;
};

export type PublicLeadInsuranceDetails = {
  memberIdOnFile: boolean;
  groupNumberOnFile: boolean;
  subscriberRelationship: SubscriberRelationship | null;
  policyholderOnFile: boolean;
};

export type PublicLeadInsuranceCard = {
  uploaded: boolean;
};

export type PublicLeadInsurance = {
  selection: PublicLeadInsuranceSelection | null;
  details: PublicLeadInsuranceDetails | null;
  eligibilityStatus: EligibilityStatus | null;
  cards: {
    front: PublicLeadInsuranceCard;
    back: PublicLeadInsuranceCard;
  } | null;
  companyName: InsuranceCompanyName | null;
};

export type PublicLeadAppointment = {
  id: AppointmentId | null;
  providerId: ProviderId;
  startTime: IsoTimestamp;
  joinUrl: Url | null;
  endTime: IsoTimestamp;
  providerName: ProviderDisplayName;
  providerPhotoUrl: Url | null;
  patientTimezone: string;
};

export type PublicLeadPayment = BookingPayment & {
  selectedMembershipPlanId: MembershipPackageId | null;
};

export type PublicLead = {
  firstName: FirstName | null;
  lastName: LastName | null;
  email: MaskedEmail | null;
  emailReadOnly: boolean;
  phone: MaskedPhone | null;
  stateCode: UsStateCode | null;
  reasonForVisitCode: ReasonForVisitCode | null;
  sponsorCode: SponsorCode | null;
  dateOfBirthOnFile: boolean;
  referralSourceOnFile: boolean;
  consentToContactViaSms: boolean | null;
  appointmentConsent: boolean | null;
  heightWeight: PublicLeadHeightWeight | null;
  insurance: PublicLeadInsurance | null;
  appointment: PublicLeadAppointment | null;
  payment: PublicLeadPayment | null;
  bookingStatus: PublicLeadBookingStatus;
};

export type CreateLeadCommand = {
  email: Email;
  input?: LeadFieldsInput;
};

export type CreateLeadCreated = {
  tag: 'created';
  lead: PublicLead;
};

export type CreateLeadMagicLinkSent = {
  tag: 'magic_link_sent';
  email: Email;
  lead: null;
  magicLinkReason: MagicLinkReason | null;
};

export type CreateLeadAlreadyBooked = {
  tag: 'already_booked';
  lead: PublicLead | null;
  errors: FieldError[];
};

export type CreateLeadValidationError = {
  tag: 'validation_error';
  lead: PublicLead | null;
  errors: FieldError[];
};

export type CreateLeadResult =
  | CreateLeadCreated
  | CreateLeadMagicLinkSent
  | CreateLeadAlreadyBooked
  | CreateLeadValidationError;

export type UpdateLeadCommand = {
  email?: Email;
  input: LeadFieldsInput;
};

export type UpdateLeadSuccess = {
  tag: 'updated';
  lead: PublicLead | null;
};

export type UpdateLeadValidationError = {
  tag: 'validation_error';
  lead: PublicLead | null;
  errors: FieldError[];
};

export type UpdateLeadMagicLinkSent = {
  tag: 'magic_link_sent';
  magicLinkReason: MagicLinkReason | null;
};

export type UpdateLeadFailed = {
  tag: 'failed';
  errors: NonEmptyArray<FieldError>;
};

export type UpdateLeadResult =
  | UpdateLeadSuccess
  | UpdateLeadValidationError
  | UpdateLeadMagicLinkSent
  | UpdateLeadFailed;

export type EligibilityStatus = EligibilityStatusFromSchema;

export type InsuranceStatus = InsuranceStatusFromSchema;

export type InsuranceStatusResult = {
  status: InsuranceStatus;
  insuranceCompanyName: InsuranceCompanyName | null;
};

export type SubmitInsuranceCommand = {
  insurance?: InsuranceSelectionInput;
  verify?: boolean;
};

export type SubmitInsuranceSuccess = {
  tag: 'submitted';
  lead: PublicLead | null;
  insuranceStatus: InsuranceStatusResult | null;
  eligibilityStatus: EligibilityStatus | null;
};

export type SubmitInsuranceValidationError = {
  tag: 'validation_error';
  lead: PublicLead | null;
  errors: FieldError[];
};

export type SubmitInsuranceResult =
  SubmitInsuranceSuccess | SubmitInsuranceValidationError;

export type AppointmentResult = {
  id: AppointmentId;
  providerId: ProviderId;
  startTime: IsoTimestamp;
  joinUrl: Url | null;
  endTime: IsoTimestamp | null;
  providerName: ProviderDisplayName | null;
  patientTimezone: string;
};

export type BookingPayment = {
  paid: boolean;
  cardOnFile: boolean;
};

export type BookingErrorCode = BookingErrorCodeFromSchema;

export type BookAppointmentCommand = {
  appointment?: AppointmentSelectionInput;
  stateCd?: UsStateCode;
  reasonForVisitCode?: ReasonForVisitCode;
  membershipPlanId?: MembershipPackageId;
  couponCode?: PromoCode;
  paymentToken?: StripeToken | null;
};

type SuccessfulBookingBase = {
  lead: PublicLead;
  appointment: AppointmentResult;
  payment: BookingPayment | null;
  retryable: false;
  // CHAS can include advisory non-ALREADY_BOOKED error codes alongside a valid appointment.
  // The booking flow treats these as successfully booked.
  errorCode: Exclude<
    BookingErrorCode,
    'ALREADY_BOOKED' | 'INITIAL_ALREADY_BOOKED'
  > | null;
};

export type BookAppointmentBooked = SuccessfulBookingBase & {
  tag: 'booked';
};

export type BookAppointmentAlreadyBooked = {
  tag: 'already_booked';
  lead: PublicLead | null;
  errors: FieldError[];
  errorCode: 'ALREADY_BOOKED' | 'INITIAL_ALREADY_BOOKED';
  retryable: false;
};

export type SuccessfulBookingResult = BookAppointmentBooked;

export type BookAppointmentRetryableFailure = {
  tag: 'retryable_failure';
  lead: PublicLead | null;
  errors: FieldError[];
  errorCode: Exclude<
    BookingErrorCode,
    'ALREADY_BOOKED' | 'INITIAL_ALREADY_BOOKED'
  > | null;
  retryable: true;
};

export type BookAppointmentFatalFailure = {
  tag: 'fatal_failure';
  lead: PublicLead | null;
  errors: FieldError[];
  errorCode: Exclude<
    BookingErrorCode,
    'ALREADY_BOOKED' | 'INITIAL_ALREADY_BOOKED'
  > | null;
  retryable: false;
};

export type BookAppointmentValidationError = {
  tag: 'validation_error';
  lead: PublicLead | null;
  errors: FieldError[];
  errorCode: Exclude<
    BookingErrorCode,
    'ALREADY_BOOKED' | 'INITIAL_ALREADY_BOOKED'
  > | null;
  retryable: false;
};

export type BookAppointmentResult =
  | SuccessfulBookingResult
  | BookAppointmentAlreadyBooked
  | BookAppointmentRetryableFailure
  | BookAppointmentFatalFailure
  | BookAppointmentValidationError;

export type PublicReasonForVisit = {
  code: ReasonForVisitCode;
  name: string;
};

export type MembershipPackage = {
  id: MembershipPackageId;
  name: MembershipPackageName;
  priceCents: number | null;
  description: MembershipPackageDescription | null;
  interval: MembershipInterval | null;
};

type PartnerEligibleInsurer = Pick<InsuranceCompany, 'id' | 'name'>;

export type PartnerConfig = {
  name: PartnerName;
  code: SponsorCode;
  brandLogoUrl: Url | null;
  creditCardRequired: boolean;
  insuranceRequired: boolean;
  codeEntryEnabled: boolean;
  ageGroup: PartnerAgeGroup;
  restrictInsurers: boolean;
  eligibleInsurers: PartnerEligibleInsurer[];
};

export type DtiConfigVariant = 'DEFAULT';

export type DtiConfigStepCondition = {
  field: DtiConfigConditionField;
  values: DtiConfigConditionValue[];
};

export type DtiConfigAction = {
  name: DtiConfigActionName;
  fields: DtiConfigFieldName[];
};

export type DtiConfigField = {
  name: DtiConfigFieldName;
  required: boolean;
  order: number;
};

export type DtiConfigStep = {
  name: DtiConfigStepName;
  order: number;
  required: boolean;
  condition: DtiConfigStepCondition | null;
  queries: DtiConfigQueryName[];
  actions: DtiConfigAction[];
  fields: DtiConfigField[];
};

export type DtiConfig = {
  variant: DtiConfigVariant;
  steps: DtiConfigStep[];
};

export type ConsumeMagicLinkCommand = {
  token: MagicLinkToken;
};

export type ConsumeLeadInviteCommand = {
  token: LeadInviteToken;
};

export type RequestMagicLinkCommand = {
  token: MagicLinkToken | LeadInviteToken;
};

export type ConsumeMagicLinkSuccess = {
  tag: 'consumed';
  lead: PublicLead;
};

export type ConsumeMagicLinkAlreadyBooked = {
  tag: 'already_booked';
  errors: FieldError[];
};

export type ConsumeMagicLinkValidationError = {
  tag: 'validation_error';
  lead: PublicLead | null;
  errors: FieldError[];
};

export type ConsumeMagicLinkFailed = {
  tag: 'failed';
  lead: PublicLead | null;
  errors: FieldError[];
};

export type ConsumeMagicLinkResult =
  | ConsumeMagicLinkSuccess
  | ConsumeMagicLinkAlreadyBooked
  | ConsumeMagicLinkValidationError
  | ConsumeMagicLinkFailed;

export type ConsumeLeadInviteResult = ConsumeMagicLinkResult;

export type RequestMagicLinkResult =
  { tag: 'accepted' } | ValidationErrorResult;

export type SearchProviderFilters = {
  ageGroups: PublicAgeGroup[];
  counselingStyles: PublicCounselingStyle[];
  reasonsForVisit: PublicReasonForVisit[];
};

export type ReferenceData = {
  insuranceCompanies: InsuranceCompany[];
  reasonsForVisit: PublicReasonForVisit[];
  membershipPackages: MembershipPackage[];
};

export type SelectMembershipPlanCommand = {
  planId: MembershipPackageId;
};

export type SelectMembershipPlanSuccess = {
  tag: 'selected';
  lead: PublicLead | null;
  selectedMembershipPlanId: MembershipPackageId;
};

export type SelectMembershipPlanValidationError = ValidationErrorResult & {
  lead: PublicLead | null;
};

export type SelectMembershipPlanResult =
  SelectMembershipPlanSuccess | SelectMembershipPlanValidationError;

export type SavePaymentMethodCommand = {
  paymentToken: StripeToken;
};

export type SavePaymentMethodResult =
  | { tag: 'saved'; lead: PublicLead | null; payment: BookingPayment }
  | (ValidationErrorResult & { lead: PublicLead | null });

export type ValidatePromoCodeCommand = {
  code: PromoCode;
  planId: MembershipPackageId;
  providerId?: ProviderId;
};

export type ValidatePromoCodeSuccess = {
  tag: 'validated';
  discountedPriceCents: number | null;
  newRecurringPriceCents: number | null;
  repeatTimes: number | null;
  couponCode: PromoCode | null;
  stillRequiresCreditCard: boolean | null;
};

export type ValidatePromoCodeResult =
  ValidatePromoCodeSuccess | ValidationErrorResult;

export type UploadInsuranceCardSide = 'FRONT' | 'BACK';

export type UploadInsuranceCardCommand = {
  side: UploadInsuranceCardSide;
  fileData: string;
  fileName: string;
  contentType: string;
};

export type UploadInsuranceCardResult =
  | {
      tag: 'uploaded';
      lead: PublicLead | null;
      fileAttachmentId: FileAttachmentId;
    }
  | (ValidationErrorResult & { lead: PublicLead | null });
